import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { hitApi } from '../commonAction';

/* Public affiliate signup popup, mounted on /affiliate-program.
 * Hits POST /auth/affiliate-signup which creates the user with role='affiliate'
 * AND a billing_affiliates row in one shot. Response is an email-verification
 * message — no JWT — so the success state just tells the user to check inbox. */
export default function AffiliateSignupModal({ show, onHide }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [paypalEmail, setPaypalEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const reset = () => {
        setName(''); setEmail(''); setPassword(''); setPaypalEmail('');
        setError(''); setSuccess(false); setSubmitting(false);
    };
    const handleHide = () => { reset(); onHide && onHide(); };

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const onSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (!name.trim() || !email.trim() || !password || !paypalEmail.trim()) {
            setError('Please fill in all required fields.');
            return;
        }
        if (!emailRe.test(email.trim())) {
            setError('Please enter a valid email address.');
            return;
        }
        if (!emailRe.test(paypalEmail.trim())) {
            setError('Please enter a valid PayPal email address.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }

        setSubmitting(true);
        hitApi({
            url: 'auth/affiliate-signup',
            method: 'POST',
            data: {
                name: name.trim(),
                email: email.trim(),
                password,
                paypalEmail: paypalEmail.trim(),
            },
            alert: false,
        }, (resp, errMsg) => {
            setSubmitting(false);
            if (resp && resp.status) {
                setSuccess(true);
            } else {
                setError(errMsg || (resp && resp.message) || 'Signup failed. Please try again.');
            }
        });
    };

    return (
        <Modal
            show={show}
            onHide={handleHide}
            animation={true}
            size="md"
            centered
            className="pxl_modalDv pxl-asm-modal"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="pxl-model-header pxl-asm-header">
                        <h2 className="modal-title pxl-asm-title">
                            {success ? 'Check your inbox' : 'Become an affiliate'}
                        </h2>
                        {!success && (
                            <p className="pxl-asm-subtitle">
                                Apply in under a minute. Most are approved within 24 hours.
                            </p>
                        )}
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="pxl_model_body pxl-asm-body">
                    {success ? (
                        <div className="pxl-asm-success">
                            <div className="pxl-asm-success-icon" aria-hidden="true">
                                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                     strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            </div>
                            <h3 className="pxl-asm-success-title">You're almost in.</h3>
                            <p className="pxl-asm-success-sub">
                                We've sent a verification link to <strong>{email}</strong>. Click it to activate
                                your account, then log in to access your affiliate dashboard.
                            </p>
                            <div className="pxl-asm-success-actions">
                                <button type="button" className="pxlCommon-btn pxl-asm-submit" onClick={handleHide}>
                                    Got it
                                </button>
                                <Link to="/login" className="pxl-asm-secondary" onClick={handleHide}>
                                    Go to log in →
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <form className="pxl-asm-form" onSubmit={onSubmit} noValidate>
                            <div className="pxl-asm-row">
                                <label className="pxl-asm-field">
                                    <span className="pxl-asm-label">Your name</span>
                                    <input
                                        type="text"
                                        className="pxl-asm-input"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Jane Doe"
                                        autoComplete="name"
                                        required
                                    />
                                </label>

                                <label className="pxl-asm-field">
                                    <span className="pxl-asm-label">Email</span>
                                    <input
                                        type="email"
                                        className="pxl-asm-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@example.com"
                                        autoComplete="email"
                                        required
                                    />
                                </label>
                            </div>

                            <label className="pxl-asm-field">
                                <span className="pxl-asm-label">Password</span>
                                <input
                                    type="password"
                                    className="pxl-asm-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 8 characters"
                                    autoComplete="new-password"
                                    required
                                    minLength={8}
                                />
                            </label>

                            <label className="pxl-asm-field">
                                <span className="pxl-asm-label">PayPal email</span>
                                <input
                                    type="email"
                                    className="pxl-asm-input"
                                    value={paypalEmail}
                                    onChange={(e) => setPaypalEmail(e.target.value)}
                                    placeholder="paypal@example.com"
                                    autoComplete="off"
                                    required
                                />
                                <div className="pxl-asm-info">
                                    <span className="pxl-asm-info-icon" aria-hidden="true">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"/>
                                            <line x1="12" y1="16" x2="12" y2="12"/>
                                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                                        </svg>
                                    </span>
                                    <span className="pxl-asm-info-text">
                                        Required for payouts. We send all affiliate commissions via PayPal —
                                        make sure this matches your verified PayPal account.
                                    </span>
                                </div>
                            </label>

                            {error && <div className="pxl-asm-error">{error}</div>}

                            <button
                                type="submit"
                                className="pxlCommon-btn pxl-asm-submit"
                                disabled={submitting}
                            >
                                {submitting ? 'Creating your account…' : 'Apply to become an affiliate'}
                            </button>

                            <p className="pxl-asm-terms">
                                By applying you agree to our{' '}
                                <Link to="/terms-conditions" className="pxl-asm-secondary">Terms</Link>
                                {' '}&amp;{' '}
                                <Link to="/privacy-policy" className="pxl-asm-secondary">Privacy Policy</Link>.
                            </p>

                            <div className="pxl-asm-foot">
                                Already have an account?{' '}
                                <Link to="/login" className="pxl-asm-secondary" onClick={handleHide}>
                                    Log in
                                </Link>
                            </div>
                        </form>
                    )}
                </div>
            </Modal.Body>
        </Modal>
    );
}
